import safeJsonParse from '@/utils/safeJsonParse';
import { useEffect, useState } from 'react';

/**
 * Function to get parsed JSON value from sessionStorage.
 * @param key session storage item key
 * @returns
 */
const getItem = <T>(key: string): T | undefined | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return safeJsonParse(window.sessionStorage.getItem(key) ?? '');
};

/**
 * Function to store a stringified JSON value against a key in sessionStorage.
 * @param key session storage item key
 * @param value session storage item value
 * @returns
 */
const setItem = <T>(key: string, value: T) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.setItem(key, JSON.stringify(value));
};

/**
 * Function to remove the item from the session storage
 * @param key session storage item key to be removed
 * @returns
 */
const removeItem = (key: string) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.removeItem(key);
};

/**
 * Function to initialize sessionStorage item for a given key.
 * If item already exists on Storage, it is returned else given initialValue is set.
 * @param key session storage item key
 * @param initialValue
 * @returns
 */
const initItem = <T>(key: string, initialValue?: T): T | null => {
  const sessionValue = getItem<T>(key);
  if (typeof sessionValue !== 'undefined') {
    return sessionValue;
  }
  setItem(key, initialValue);
  return initialValue ?? null;
};
/**
 * Hook to access session storage and setting it in state
 * @param key against which the data should be stored inside the storage
 * @param initialValue value to be initialized with
 * @returns [state, stateSetter, removeItemFromStorage]
 */
const useSessionStorage = <T>(
  key: string,
  initialValue?: T
): [state: T | null, setter: (value: T) => void, removeItem: () => void] => {
  const [state, setState] = useState(() => initItem(key, initialValue));

  // re-initialize storage item on key change (for dynamic keys)
  useEffect(() => {
    const sessionValue = initItem<T>(key, initialValue);
    setItem(key, sessionValue);
    setState(sessionValue);
  }, [key, initialValue]);

  if (typeof window === 'undefined') {
    return [state, function () {}, function () {}];
  }

  const setter = (value: T): void => {
    setItem<T>(key, value);
    setState(value);
  };

  return [
    state,
    setter,
    () => {
      setState(null);
      removeItem(key);
    },
  ];
};

export default useSessionStorage;
